import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownOption {
    value: string;
    label: string;
}

interface DropdownProps {
    value: string;
    options: DropdownOption[];
    onChange: (value: string) => void;
    allLabel: string;
    compact?: boolean;
    /** Show a search box above the options — turn on once there are more than a handful. */
    searchable?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({ value, options, onChange, allLabel, compact = false, searchable = false }) => {
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
    const [listMaxHeight, setListMaxHeight] = useState(280);
    const [query, setQuery] = useState('');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onOutsideClick);
        return () => document.removeEventListener('mousedown', onOutsideClick);
    }, [open]);

    const selected = options.find(o => o.value === value);
    const triggerLabel = selected ? selected.label : allLabel;

    const handleToggle = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const top = rect.bottom + 6;
            setPanelStyle({ position: 'fixed', top, left: rect.left, minWidth: rect.width, zIndex: 9999 });
            setListMaxHeight(Math.max(160, Math.min(280, window.innerHeight - top - 24)));
            setQuery('');
        }
        setOpen(o => !o);
    };

    const q = query.trim().toLowerCase();
    const filteredOptions = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
    const showAll = !q || allLabel.toLowerCase().includes(q);

    const rowClass = (isSelected: boolean) =>
        `flex items-center gap-2 w-full px-3 py-[9px] rounded-[9px] text-left text-[13px] font-semibold ${
            isSelected ? 'bg-mapper-teal-bg-3 text-mapper-teal-dark' : 'text-mapper-text'
        }`;

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                onClick={handleToggle}
                className={
                    compact
                        ? "w-full flex items-center justify-between gap-2 px-2 py-[6px] border border-mapper-border-2 rounded-[8px] text-[12px] bg-mapper-white text-mapper-body-text"
                        : "flex items-center justify-between gap-2 px-3 py-[9px] border border-mapper-border-2 rounded-[10px] text-[13px] bg-mapper-white text-mapper-body-text min-w-[140px]"
                }
            >
                <span className="truncate">{triggerLabel}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-mapper-muted-3 shrink-0">
                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && createPortal(
                <div
                    ref={panelRef}
                    style={panelStyle}
                    className="mapper-ui font-sans bg-mapper-white border border-mapper-border-5 rounded-xl shadow-mapper-dropdown overflow-hidden"
                >
                    {searchable && (
                        <div className="p-[6px] pb-0">
                            <div className="relative">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#A6AEBA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-[9px] top-1/2 -translate-y-1/2">
                                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                                </svg>
                                <input
                                    autoFocus
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Search…"
                                    className="w-full box-border pl-[28px] pr-2 py-[7px] mb-[6px] border border-mapper-border-2 rounded-[8px] text-[12.5px] outline-none bg-white"
                                />
                            </div>
                        </div>
                    )}
                    <div className={`overflow-y-auto p-[6px] ${searchable ? 'pt-0' : ''}`} style={{ maxHeight: listMaxHeight }}>
                        {showAll && (
                            <button onClick={() => { onChange(''); setOpen(false); }} className={rowClass(value === '')}>
                                {value === '' && (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                )}
                                <span className={value === '' ? '' : 'ml-[22px]'}>{allLabel}</span>
                            </button>
                        )}
                        {filteredOptions.map(opt => (
                            <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }} className={rowClass(value === opt.value)}>
                                {value === opt.value && (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                )}
                                <span className={value === opt.value ? '' : 'ml-[22px]'}>{opt.label}</span>
                            </button>
                        ))}
                        {!showAll && filteredOptions.length === 0 && (
                            <div className="px-3 py-[14px] text-[12.5px] text-mapper-muted-2 text-center">No matches</div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
